import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import FollowersModal from './FollowersModal';

interface UserProfileData {
  id: string;
  name: string;
  handle: string;
  picture?: string;  headline: string;
  bio: string;
  bannerImage: string;
  creationDate: string;
  followerCount?: number;
  followingCount?: number;
}

interface Post {
  _id: string;
  content: string;
  user: {
    name: string;
    handle: string;
    picture: string;
  };
  createdAt: string;
  updatedAt: string;
  deleted?: boolean;
}

const UserProfile: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, getUserByHandle, updateProfile, clearUserCache, followUser, unfollowUser, checkFollowStatus } = useAuth();
  const { addToast } = useToast();
    // Extract handle from URL - only support /@handle format
  // Use useMemo to prevent recalculation on every render
  const handle = useMemo(() => {
    // Only handle @ format like /@handle
    const pathname = location.pathname;
    if (pathname.startsWith('/@')) {
      return pathname.substring(2); // Remove the /@
    }
    
    return null;
  }, [location.pathname]);

  // Scroll to top when the route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Check if this is the current user's own profile
  const isOwnProfile = useMemo(() => {
    return user && handle === user.handle;
  }, [user, handle]);
  const [userProfile, setUserProfile] = useState<UserProfileData | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const currentRequestRef = useRef<string | null>(null); // Track current request to prevent duplicates
    // Edit mode state (only for own profile)
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Form state for editing (using state instead of refs for better React integration)
  const [formData, setFormData] = useState({
    displayName: '',
    headline: '',
    bio: '',
    bannerImage: ''
  });    // Handle validation state (similar to ProfileSetup) - Removed since handle editing moved to settings
    // Follow functionality state
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  
  // Modal state for followers/following lists
  const [isFollowersModalOpen, setIsFollowersModalOpen] = useState(false);
  const [isFollowingModalOpen, setIsFollowingModalOpen] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
    const fetchUserProfile = useCallback(async (targetHandle: string, forceRefresh: boolean = false) => {
    // Prevent duplicate requests for the same handle
    if (currentRequestRef.current === targetHandle) {
      console.log('Request already in progress for handle:', targetHandle);
      return;
    }
    
    currentRequestRef.current = targetHandle;
    console.log('Fetching profile for handle:', targetHandle);
    
    try {
      setLoading(true);
      setError(null);
      
      const profile = await getUserByHandle(targetHandle, forceRefresh);
      
      // Check if this is still the current request (component might have unmounted or handle changed)
      if (currentRequestRef.current !== targetHandle) {
        console.log('Request cancelled for handle:', targetHandle);
        return;
      }
        if (profile) {        // Convert User type to UserProfileData (remove sensitive fields)
        const profileData: UserProfileData = {
          id: profile.id,
          name: profile.name,
          handle: profile.handle,
          picture: profile.picture,
          headline: profile.headline,
          bio: profile.bio,
          bannerImage: profile.bannerImage,
          creationDate: profile.creationDate,
          followerCount: (profile as any).followerCount || 0,
          followingCount: (profile as any).followingCount || 0
        };
        console.log('Profile data fetched successfully:', profileData);
        setUserProfile(profileData);
        
        // Update follower counts if this profile has them
        if (typeof (profile as any).followerCount === 'number') {
          setFollowerCount((profile as any).followerCount);
        }
        if (typeof (profile as any).followingCount === 'number') {
          setFollowingCount((profile as any).followingCount);
        }
        
        setError(null);
      } else {
        console.log('Profile not found for handle:', targetHandle);
        setError('User not found');
      }
    } catch (err) {
      // Only update state if this is still the current request
      if (currentRequestRef.current === targetHandle) {
        console.error('Error fetching user profile:', err);
        setError('Failed to load user profile');
        addToast('Failed to load user profile', 'error');
      }
    } finally {
      // Only update loading state if this is still the current request
      if (currentRequestRef.current === targetHandle) {
        setLoading(false);
        currentRequestRef.current = null; // Clear the current request
      }
    }  }, [getUserByHandle, addToast]);

  // Fetch posts for the user
  const fetchUserPosts = useCallback(async (targetHandle: string) => {
    if (!targetHandle) return;
    
    setPostsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/posts/user/${targetHandle}`);
      const data = await response.json();
      
      if (data.status === 'success') {
        setPosts(data.data.posts || []);
      } else {
        console.error('Failed to fetch posts:', data.message);
        setPosts([]);
      }
    } catch (error) {
      console.error('Error fetching user posts:', error);
      setPosts([]);
    } finally {
      setPostsLoading(false);
    }
  }, []);

  // Fetch follow status for the profile
  const fetchFollowStatus = useCallback(async (targetHandle: string) => {
    if (!targetHandle || !user) return;
    
    try {
      const status = await checkFollowStatus(targetHandle);
      if (status) {
        setIsFollowing(status.isFollowing);
        setFollowerCount(status.followerCount);
        setFollowingCount(status.followingCount);
      }
    } catch (error) {
      console.error('Error fetching follow status:', error);
    }
  }, [checkFollowStatus, user]);

  // Handle follow/unfollow toggle
  const handleFollowToggle = useCallback(async () => {
    if (!handle || followLoading) return;
    
    setFollowLoading(true);
    
    try {
      let success: boolean;
      if (isFollowing) {
        success = await unfollowUser(handle);
        if (success) {
          setIsFollowing(false);
          setFollowerCount(prev => Math.max(0, prev - 1));
          addToast(`Unfollowed @${handle}`, 'success');
        } else {
          addToast('Failed to unfollow user', 'error');
        }
      } else {
        success = await followUser(handle);
        if (success) {
          setIsFollowing(true);
          setFollowerCount(prev => prev + 1);
          addToast(`Now following @${handle}`, 'success');
        } else {
          addToast('Failed to follow user', 'error');
        }
      }
    } catch (error) {
      console.error('Error toggling follow status:', error);
      addToast('An error occurred', 'error');
    } finally {
      setFollowLoading(false);
    }
  }, [handle, isFollowing, followLoading, followUser, unfollowUser, addToast]);
  
  useEffect(() => {
    console.log('UserProfile useEffect triggered with handle:', handle);
    
    if (!handle) {
      console.log('No handle provided');
      setError('No handle provided');
      setLoading(false);
      return;
    }    // Force refresh for own profile to ensure latest data, use cache for others
    const shouldForceRefresh = user && handle === user.handle;
    console.log('Should force refresh for own profile:', shouldForceRefresh);
    
    // If this is the user's own profile, clear any cached data first to ensure fresh fetch
    if (shouldForceRefresh) {
      console.log('Clearing cache for own profile before fetch');
      clearUserCache(handle);
    }      fetchUserProfile(handle, Boolean(shouldForceRefresh));
    
    // Also fetch the user's posts
    fetchUserPosts(handle);
    
    // Fetch follow status if this is not the user's own profile
    if (!shouldForceRefresh && user) {
      fetchFollowStatus(handle);
    }
    
    // Cleanup function to cancel any ongoing request
    return () => {
      console.log('UserProfile useEffect cleanup for handle:', handle);
      if (currentRequestRef.current === handle) {
        currentRequestRef.current = null;
      }
    };  }, [handle, user, fetchUserProfile, fetchUserPosts, fetchFollowStatus, clearUserCache]); // Depend on handle, user, and the memoized functions
  // Handle save profile for own profile
  const handleSaveProfile = useCallback(async () => {
    if (!isOwnProfile || !updateProfile || !userProfile) return;
    
    setIsSaving(true);
    
    try {
      const profileData = {
        name: formData.displayName.trim() || '',
        headline: formData.headline.trim() || '',
        bio: formData.bio.trim() || '',
        bannerImage: formData.bannerImage.trim()
      };

      // Optimistically update the UI first for instant feedback
      const optimisticProfile: UserProfileData = {
        ...userProfile,
        name: profileData.name,
        headline: profileData.headline,
        bio: profileData.bio,
        bannerImage: profileData.bannerImage
      };
      setUserProfile(optimisticProfile);      const success = await updateProfile(profileData);      if (success) {
        setIsEditing(false);
        addToast('Profile updated successfully!', 'success');
        
        // Fetch the latest data from the server to confirm the update
        // This will overwrite the optimistic update with the server data
        if (handle) {
          console.log('Profile updated successfully, confirming with server data for handle:', handle);
          fetchUserProfile(handle, true); // Force refresh to get confirmed server data
        }
      } else {
        // Revert the optimistic update on failure
        if (handle) {
          fetchUserProfile(handle, true);
        }
        addToast('Failed to update profile. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      // Revert the optimistic update on error
      if (handle) {
        fetchUserProfile(handle, true);
      }
      addToast('An error occurred while updating your profile.', 'error');
    } finally {
      setIsSaving(false);
    }  }, [isOwnProfile, updateProfile, formData, handle, addToast, fetchUserProfile, userProfile]);

  // Update form fields when profile data changes (for own profile)
  useEffect(() => {
    if (isOwnProfile && userProfile) {
      setFormData({
        displayName: userProfile.name || '',
        headline: userProfile.headline || '',
        bio: userProfile.bio || '',
        bannerImage: userProfile.bannerImage || ''
      });
    }
  }, [isOwnProfile, userProfile]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin h-8 w-8 border-2 border-custom-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }
  if (error || !userProfile) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen py-8 px-4">
        <Card className="w-full max-w-md bg-paper shadow-paper border border-parchment-300">
          <CardContent className="text-center p-8">
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              {error === 'User not found' ? 'User Not Found' : 'Error'}
            </h1>
            <p className="text-slate-600 mb-6">
              {error === 'User not found' 
                ? `The user @${handle} could not be found.`
                : error || 'Something went wrong while loading the profile.'
              }
            </p>
            <button
              onClick={() => navigate('/feed')}
              className="px-4 py-2 bg-custom-blue-600 text-white rounded-md hover:bg-custom-blue-700 transition-colors"
            >
              Return to the Feed
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center justify-start min-h-screen py-8">
      <main className="flex flex-col items-center justify-center w-full flex-1 px-4 md:px-20">
        <div className="w-full max-w-4xl bg-paper rounded-lg shadow-paper border border-parchment-300 overflow-hidden">
          {/* Profile Banner and Picture */}
          <div className="relative">            <img 
              src={formData.bannerImage || userProfile.bannerImage} 
              alt="Profile Banner" 
              className="w-full h-72 object-cover" 
            />
            <div className="absolute -bottom-16 left-8">
              <Avatar className="w-32 h-32 border-4 border-paper shadow-lg">
                <AvatarImage src={userProfile.picture} alt="Profile Picture" />
                <AvatarFallback className="text-2xl bg-parchment-100 text-custom-blue-900">
                  {userProfile.name.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </div>            {/* Follower/Following Counts */}
            <div className="absolute right-6 top-6 flex flex-col items-end bg-parchment-50/90 backdrop-blur-sm rounded-md p-3 border border-parchment-300 shadow-paper">
              <div className="text-custom-blue-900 text-sm font-medium space-y-1">
                <button
                  onClick={() => setIsFollowersModalOpen(true)}
                  className="text-right hover:text-custom-blue-700 transition-colors cursor-pointer"
                >
                  <span className="font-semibold">{followerCount}</span> Followers
                </button>
                <br></br>
                <button
                  onClick={() => setIsFollowingModalOpen(true)}
                  className="text-right hover:text-custom-blue-700 transition-colors cursor-pointer"
                >
                  <span className="font-semibold">{followingCount}</span> Following
                </button>
              </div>
            </div>{/* Edit Button - Only show for own profile */}
            {isOwnProfile && (
              <div className="absolute left-6 top-6">
                {!isEditing && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setIsEditing(true)}
                    className="bg-parchment-50/90 backdrop-blur-sm border-parchment-400 text-custom-blue-900 font-semibold hover:bg-parchment-100 hover:text-custom-blue-800 shadow-paper"
                  >
                    Edit Profile
                  </Button>
                )}
              </div>
            )}

            {/* Follow/Unfollow Button - Only show for other users' profiles */}
            {!isOwnProfile && user && (
              <div className="absolute left-6 top-6">
                <Button 
                  variant={isFollowing ? "outline" : "default"}
                  size="sm" 
                  onClick={handleFollowToggle}
                  disabled={followLoading}
                  className={
                    isFollowing 
                      ? "bg-parchment-50/90 backdrop-blur-sm border-parchment-400 text-custom-blue-900 font-semibold hover:bg-red-50 hover:text-red-700 hover:border-red-300 shadow-paper"
                      : "bg-custom-blue-600 hover:bg-custom-blue-700 text-white font-semibold shadow-paper"
                  }
                >
                  {followLoading ? 'Loading...' : (isFollowing ? 'Unfollow' : 'Follow')}
                </Button>
              </div>
            )}
          </div>

          {/* Profile Information */}
          <div className="px-6 pt-16 pb-6">
            <div className="flex flex-row">
              <div className="w-1/4" />
              <div className="w-3/4 text-left">                {isOwnProfile && isEditing ? (
                  <>
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-1 text-custom-blue-900">Display Name</label>
                      <input
                        type="text"
                        maxLength={50}
                        value={formData.displayName}
                        onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                        className="w-full px-3 py-2 border border-parchment-300 rounded-md bg-paper text-custom-blue-900 placeholder-custom-blue-500"
                      />
                    </div>                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-1 text-custom-blue-900">Headline</label>
                      <input
                        type="text"
                        maxLength={100}
                        value={formData.headline}
                        onChange={(e) => setFormData(prev => ({ ...prev, headline: e.target.value }))}
                        className="w-full px-3 py-2 border border-parchment-300 rounded-md bg-paper text-custom-blue-900 placeholder-custom-blue-500"
                        placeholder="A short headline about yourself..."
                      />
                      <div className="text-xs text-custom-blue-600 mt-1">
                        {formData.headline.length}/100 characters
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-1 text-custom-blue-900">Bio</label>
                      <textarea
                        maxLength={500}
                        value={formData.bio}
                        onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                        className="w-full px-3 py-2 border border-parchment-300 rounded-md bg-paper h-24 resize-none text-custom-blue-900 placeholder-custom-blue-500"
                        placeholder="Tell us about yourself..."
                      />
                      <div className="text-xs text-custom-blue-600 mt-1">
                        {formData.bio.length}/500 characters
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-1 text-custom-blue-900">Banner Image URL</label>
                      <input
                        type="url"
                        value={formData.bannerImage}
                        onChange={(e) => setFormData(prev => ({ ...prev, bannerImage: e.target.value }))}
                        className="w-full px-3 py-2 border border-parchment-300 rounded-md bg-paper text-custom-blue-900 placeholder-custom-blue-500"
                        placeholder="https://example.com/banner-image.jpg"
                      />                    </div>
                  </>
                ) : (
                  <>
                    <h1 className="text-3xl font-bold text-custom-blue-900 mb-2">{userProfile.name}</h1>
                    <p className="text-lg text-custom-blue-600 mb-2">@{userProfile.handle}</p>
                    {userProfile.headline && (
                      <p className="text-xl text-custom-blue-800 font-medium mb-2">
                        {userProfile.headline}
                      </p>
                    )}
                    <div className="text-sm text-custom-blue-700">
                      <div className="flex items-center space-x-4 mt-4">
                        <span>
                          📅 Joined {new Date(userProfile.creationDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long'
                          })}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>          {/* Profile Tabs - Hidden when editing */}
          {!(isOwnProfile && isEditing) && (
            <div className="px-6 pb-6">              <Tabs defaultValue="posts" className="w-full">
                <TabsList className="grid w-full grid-cols-3 bg-parchment-50 border border-parchment-300 p-1 shadow-paper">
                  <TabsTrigger 
                    value="posts" 
                    className="text-custom-blue-600 data-[state=active]:bg-parchment-100 data-[state=active]:text-custom-blue-900 data-[state=active]:shadow-paper transition-all duration-200 hover:text-custom-blue-800 hover:bg-parchment-50"
                  >
                    Posts
                  </TabsTrigger>
                  <TabsTrigger 
                    value="projects" 
                    className="text-custom-blue-600 data-[state=active]:bg-parchment-100 data-[state=active]:text-custom-blue-900 data-[state=active]:shadow-paper transition-all duration-200 hover:text-custom-blue-800 hover:bg-parchment-50"
                  >
                    Projects
                  </TabsTrigger>
                  <TabsTrigger 
                    value="biography" 
                    className="text-custom-blue-600 data-[state=active]:bg-parchment-100 data-[state=active]:text-custom-blue-900 data-[state=active]:shadow-paper transition-all duration-200 hover:text-custom-blue-800 hover:bg-parchment-50"
                  >
                    Biography
                  </TabsTrigger>

                </TabsList>                  <TabsContent value="posts" className="mt-4">
                  <Card className="bg-parchment-50 border-parchment-200">
                    <CardHeader>
                      <CardTitle className="text-custom-blue-900">Posts</CardTitle>
                      <CardDescription className="text-custom-blue-600">
                        {isOwnProfile ? 'My Posts' : `${userProfile.name}'s Posts`}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {postsLoading ? (
                        <div className="flex justify-center py-8">
                          <div className="animate-spin h-6 w-6 border-2 border-custom-blue-600 border-t-transparent rounded-full"></div>
                        </div>
                      ) : posts.length > 0 ? (
                        <div className="space-y-4">
                          {posts.map((post) => (
                            <Card key={post._id} className="bg-paper border-parchment-300 shadow-paper">
                              <CardContent className="p-4">
                                <p className="text-custom-blue-800 whitespace-pre-wrap mb-3">
                                  {post.content}
                                </p>                                <div className="text-sm text-custom-blue-600">
                                  {new Date(post.createdAt).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-custom-blue-600">
                          <p>
                            {isOwnProfile 
                              ? 'No posts yet. Write your first post!' 
                              : 'No posts yet. Check back later!'
                            }
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="projects" className="mt-4">
                  <Card className="bg-parchment-50 border-parchment-300 text-custom-blue-900 shadow-paper">
                    <CardHeader>
                      <CardTitle className="text-custom-blue-900">
                        Projects
                      </CardTitle>
                      <CardDescription className="text-custom-blue-700">
                        {isOwnProfile ? 'My Projects' : `${userProfile.name}'s Projects`}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="text-center py-8 text-custom-blue-600">
                        <p>
                          {isOwnProfile 
                            ? 'No projects yet. Start creating your first project!' 
                            : 'No projects yet. Check back later!'
                          }
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="biography" className="mt-4">
                  <Card className="bg-parchment-50 border-parchment-300 text-custom-blue-900 shadow-paper">
                    <CardHeader>
                      <CardTitle className="text-custom-blue-900">
                        Biography
                      </CardTitle>
                    <CardDescription className="text-custom-blue-700">
                        {isOwnProfile ? 'My Bio' : `${userProfile.name}'s Bio`}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {userProfile.bio ? (
                          <p className="text-custom-blue-700 whitespace-pre-wrap">
                            {userProfile.bio}
                          </p>
                        ) : (
                          isOwnProfile ? (
                            <div className="italic text-center py-8 text-custom-blue-500 cursor-pointer hover:text-custom-blue-600" 
                                  onClick={() => setIsEditing(true)}>
                              Write a bio for yourself by clicking here!
                            </div>
                          ) : (
                            <div className="text-center py-8 text-custom-blue-600">
                              <p>
                                No bio yet. Check back later!
                              </p>
                            </div>
                          )
                        )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          )}

          {/* Action Buttons - Only for own profile when editing */}
          {isOwnProfile && (
            <div className="px-6 pb-6">
              {isEditing ? (
                <div className="mt-6 flex justify-end gap-2">                  <Button 
                    variant="secondary"                    onClick={() => {
                      setIsEditing(false);
                      // Reset form state to current profile values
                      setFormData({
                        displayName: userProfile.name || '',
                        headline: userProfile.headline || '',
                        bio: userProfile.bio || '',
                        bannerImage: userProfile.bannerImage || ''
                      });
                    }}
                    disabled={isSaving}
                    className="bg-parchment-100 border-parchment-300 text-custom-blue-900 hover:bg-parchment-200"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSaveProfile}
                    disabled={isSaving}
                    className="bg-custom-blue-600 hover:bg-custom-blue-700 text-white"
                  >
                    {isSaving ? 'Saving...' : 'Save Profile'}
                  </Button>
                </div>
              ) : (
                <div className="mt-6 flex justify-end">
                </div>
              )}
            </div>
          )}        </div>
      </main>      {/* Followers Modal */}
      <FollowersModal
        key="followers-modal"
        isOpen={isFollowersModalOpen}
        onClose={() => setIsFollowersModalOpen(false)}
        userHandle={handle || ''}
        initialTab="followers"
        userName={userProfile?.name || ''}
      />

      {/* Following Modal */}
      <FollowersModal
        key="following-modal"
        isOpen={isFollowingModalOpen}
        onClose={() => setIsFollowingModalOpen(false)}
        userHandle={handle || ''}
        initialTab="following"
        userName={userProfile?.name || ''}
      />
    </div>
  );
};

export default UserProfile;
